// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

/**
 * Deep Agent registry — the INTENTIONAL LOCAL AUTHORITY for DeepAgent
 * route + page metadata (PRE-001 Route B closure bar).
 *
 * Scope split (this is the agreed closure bar — see
 * deepAgentRegistry.authorityContract.test.ts):
 *   - Backend `control_plane` authority owns: config identity defaults, runtime
 *     install/info/health routing, command-surface backend descriptors.
 *   - This frontend registry owns: React Router route paths + lazy page
 *     components (`App.tsx` maps `DEEP_AGENTS` into `<Route>` elements).
 *
 * This registry is NOT a backend-authority copy and PRE-001 does not claim
 * the frontend derives from one cross-layer authority. Route/page metadata is
 * locally owned because each entry's `page` is a lazy React `ComponentType`
 * with no backend equivalent, and React Router needs the route paths at
 * module-load time (before any backend fetch). The identity values the
 * registry ALSO carries (agentId/runtimeUrl/slash) are a cross-layer DRIFT LOCK
 * — pinned by contract tests to the backend `DeepAgentEntryConfig` values, as a
 * deliberate reviewed change surface, not as a derivation obligation.
 *
 * Adding a new Deep Agent:
 * 1. Add an entry to `DEEP_AGENTS` below (carry path/slash/page/label/...).
 * 2. Create the page component in `src/pages/`.
 * 3. Routes and shell wiring are generated automatically from this registry.
 * 4. Keep the identity values in lockstep with the backend authority — the
 *    contract test will fail if they drift.
 */

import type { ComponentType } from 'react';

export interface DeepAgentEntry {
  /** Capability key used by the shell and capability runtime. */
  key: string;
  /** Agent identifier matching the backend graph name. */
  agentId: string;
  /** AG-UI runtime URL for this agent. */
  runtimeUrl: string;
  /** React Router path (must start with '/'). */
  path: string;
  /** Slash command that resolves to a deepagent_handoff (== path). */
  slash: string;
  /** Human-readable label for palette / cards. */
  label: string;
  /** Short description of the workbench surface. */
  description: string;
  /** Short aliases for palette search. */
  aliases: string[];
  /** Lazy-loaded page component. */
  page: ComponentType;
}

// ── Lazy page imports ──────────────────────────────────────────────────
import { lazy } from 'react';

const TeacherWorkbenchPage = lazy(() => import('@/pages/TeacherWorkbenchPage'));
const ResearchAssistantPage = lazy(() => import('@/pages/ResearchAssistantPage'));
const SandboxAnalysisPage = lazy(() => import('@/pages/SandboxAnalysisPage'));
const ModelingWorkbenchPage = lazy(() => import('@/pages/ModelingWorkbenchPage'));
const PaperWorkbenchPage = lazy(() => import('@/pages/PaperWorkbenchPage'));

// ── Registry ───────────────────────────────────────────────────────────
// Each entry carries its full route/page metadata inline — no separate
// label/slash/alias dictionaries elsewhere. Identity values (agentId/runtimeUrl)
// are a cross-layer drift lock against the backend control_plane authority.
export const DEEP_AGENTS: DeepAgentEntry[] = [
  {
    key: 'teacher_workbench',
    agentId: 'teacher_workbench_agent',
    runtimeUrl: '/api/v1/chat/deepagents/teacher-workbench',
    path: '/teacher-workbench',
    slash: '/teacher-workbench',
    label: 'Teacher Workbench',
    description: '长任务教案、讨论链、实验方案、教师产物发布',
    aliases: ['/teacher', '/teacher-workbench'],
    page: TeacherWorkbenchPage,
  },
  {
    key: 'research_assistant',
    agentId: 'research_assistant_agent',
    runtimeUrl: '/api/v1/chat/deepagents/research-assistant',
    path: '/research-assistant',
    slash: '/research-assistant',
    label: 'Research Assistant',
    description: '文献检索、资源综合、研究报告、参考文献',
    aliases: ['/research', '/research-assistant'],
    page: ResearchAssistantPage,
  },
  {
    key: 'sandbox_analysis',
    agentId: 'sandbox_analysis_agent',
    runtimeUrl: '/api/v1/chat/deepagents/sandbox-analysis',
    path: '/sandbox-analysis',
    slash: '/sandbox-analysis',
    label: 'Sandbox Analysis',
    description: 'Python 沙箱、物理仿真、数据分析、绘图和报告',
    aliases: ['/sandbox', '/sandbox-analysis'],
    page: SandboxAnalysisPage,
  },
  {
    key: 'modeling_workbench',
    agentId: 'modeling_workbench_agent',
    runtimeUrl: '/api/v1/chat/deepagents/modeling-workbench',
    path: '/modeling-workbench',
    slash: '/modeling-workbench',
    label: 'Modeling Workbench',
    description: '问题分解、数值仿真、建模产物与建模报告',
    aliases: ['/modeling', '/modeling-workbench'],
    page: ModelingWorkbenchPage,
  },
  {
    key: 'paper_workbench',
    agentId: 'paper_workbench_agent',
    runtimeUrl: '/api/v1/chat/deepagents/paper-workbench',
    path: '/paper-workbench',
    slash: '/paper-workbench',
    label: 'Paper Workbench',
    description: '需求澄清、文献发现、论文起草、引用核验与最终论文包发布',
    aliases: ['/paper', '/paper-workbench'],
    page: PaperWorkbenchPage,
  },
] as const;

/** Look up a registry entry by capability key (optional — returns undefined if not found). */
export function findDeepAgent(key: string): DeepAgentEntry | undefined {
  return DEEP_AGENTS.find((a) => a.key === key);
}

/** Assert a registry entry exists and return it. Throws if the key is unknown. */
export function assertDeepAgent(key: string): DeepAgentEntry {
  const entry = findDeepAgent(key);
  if (!entry) {
    throw new Error(
      `DeepAgent "${key}" not found in registry. Known keys: ${DEEP_AGENTS.map((a) => a.key).join(', ')}`,
    );
  }
  return entry;
}

/** Resolve a registry entry — same as assertDeepAgent (alias for readability). */
export const resolveDeepAgent = assertDeepAgent;

/** All registered capability keys. */
export type DeepAgentKey = (typeof DEEP_AGENTS)[number]['key'];

/**
 * Machine-checked declaration of this registry's authority scope (Route B).
 *
 * Value `'intentional_local_authority'` encodes the agreed PRE-001 closure bar:
 * the frontend route/page registry is an intentional LOCAL authority, not a
 * backend-authority derivation. Backend `control_plane` scope is limited to config
 * identity defaults + runtime install/info/health + command-surface
 * descriptors. The identity values this registry also carries are a
 * cross-layer drift lock (contract-pinned), not a derivation.
 *
 * Pinned by deepAgentRegistry.authorityContract.test.ts. If a future wave
 * establishes a real codegen derivation chain (Route A), flip this to
 * `'derived'` and update the no-contradiction contract test accordingly.
 */
export const DEEP_AGENT_REGISTRY_DERIVATION_STATUS = 'intentional_local_authority' as const;

